/**
 * installPreloadErrorReload — global vite:preloadError handler.
 *
 * WHY THIS EXISTS
 * ───────────────
 * The app code-splits (lazy() routes + GoldenBootPanel). After a redeploy the
 * old chunk hashes are gone from the host. A user with a stale tab open gets a
 * 404 on the next dynamic import, Vite fires `vite:preloadError`, and the
 * component tree hangs. The documented Vite remedy is to reload so the browser
 * re-fetches index.html (which references the current chunk hashes).
 *
 * LOOP GUARD (time-windowed, persisted)
 * ─────────────────────────────────────
 * A naive "reload on every preloadError" would infinite-loop if a chunk is
 * GENUINELY missing (an infra problem a reload can't fix). We record the last
 * reload time in sessionStorage and suppress another reload within
 * RELOAD_WINDOW_MS — that short window is the tight-loop signature (reload →
 * immediate re-error). A guard keyed on TIME (not a one-shot boolean) still
 * allows recovery from a LATER, distinct redeploy in the same tab session,
 * which matters during a multi-week tournament where several deploys can land
 * while a tab stays open.
 *
 * The guard MUST survive the reload to work, so it lives in sessionStorage. If
 * sessionStorage is unavailable (SecurityError in strict privacy modes /
 * sandboxed iframes) we CANNOT persist a cross-reload guard — reloading anyway
 * would risk an unbounded loop on a genuinely-missing chunk — so we do nothing
 * and let the import error propagate to the app-level ErrorBoundary (see
 * App.jsx), which renders a recoverable fallback (retry + manual reload). All
 * sessionStorage access is wrapped so the handler can never crash.
 *
 * TELEMETRY
 * ─────────
 * Each outcome emits a best-effort, low-cardinality GA event (plus a console
 * breadcrumb) so a reload loop or a storage-blocked recovery is visible to ops
 * instead of relying on user reports. Telemetry is wrapped and can never throw
 * out of the handler.
 *
 * USAGE
 * ─────
 * Call `installPreloadErrorReload()` once in main.jsx, near initAnalytics().
 * The returned cleanup function is only needed in tests; the production app
 * never needs to remove the listener.
 *
 * @param {Window} [win=window] - injectable for testing (stub location.reload + sessionStorage + gtag)
 * @param {() => number} [now] - injectable clock (ms since epoch) for deterministic tests
 * @returns {() => void} cleanup - removes the event listener
 */
const GUARD_KEY = 'vite_preload_error_reload_at';
const RELOAD_WINDOW_MS = 10_000;

export function installPreloadErrorReload(win = window, now = () => Date.now()) {
  // Best-effort, low-cardinality signal so ops can see preload-error recovery —
  // and any reload loop — instead of relying on user reports. Never throws.
  function report(reason) {
    try {
      win.gtag?.('event', 'preload_error_reload', { reason });
    } catch {
      /* ignore telemetry failures */
    }
    try {
      win.console?.warn?.(`[preloadErrorReload] ${reason}`);
    } catch {
      /* ignore */
    }
  }

  function handlePreloadError(event) {
    let last;
    try {
      last = Number(win.sessionStorage.getItem(GUARD_KEY)) || 0;
    } catch {
      // sessionStorage unavailable — cannot persist a cross-reload guard, and
      // reloading without one risks an infinite loop on a genuinely-missing
      // chunk. Do nothing and let the import error propagate to the app-level
      // ErrorBoundary (which offers retry + a manual reload).
      report('storage_unavailable');
      return;
    }

    const current = now();
    // Suppress a reload within RELOAD_WINDOW_MS of the previous one: the
    // tight-loop signature of a genuinely-missing chunk a reload can't fix. We
    // deliberately do NOT preventDefault here, so the underlying import error
    // still propagates and surfaces the real failure.
    if (last && current - last < RELOAD_WINDOW_MS) {
      report('loop_suppressed');
      return;
    }

    try {
      win.sessionStorage.setItem(GUARD_KEY, String(current));
    } catch {
      // Lost write access between read and write — same reasoning as the read
      // path: don't reload without a persistable guard.
      report('storage_unavailable');
      return;
    }

    // Recover by reloading; suppress the thrown import error since the reload
    // re-fetches index.html with the current chunk hashes.
    report('reload');
    event?.preventDefault?.();
    win.location.reload();
  }

  win.addEventListener('vite:preloadError', handlePreloadError);

  return function cleanup() {
    win.removeEventListener('vite:preloadError', handlePreloadError);
  };
}
